<template>
    <div @click.self="onClick" :class="{ 'show-border': props.showBorder }" class="chip-input">
        <div ref="chipsHolder" class="chips-holder" :class="{ 'no-wrap': !props.wrap }">
            <div v-for="(c, index) in visibleChips" :key="index"
                :class="{ 'no-remove': !chipDelete, ['size-' + props.size]: true }" class="chip"
                :style="{ backgroundColor: getChipColor(index), color: getChipTextColor(index) }">{{ c }}
                <button v-if="props.showDelete" @click="removeChip(index)" class="chip-remove-button"
                    aria-label="Remove chip">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" :color="getChipTextColor(index)"
                        stroke-width="2" class="chip-remove-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div v-if="!props.wrap && hasOverflow && overflowCount > 0" class="overflow-indicator"
                :class="['size-' + props.size]">
                +{{ overflowCount }}
            </div>
            <input v-if="props.editable" ref="chipInput" type="text" :style="{ width: inputValue.length + 3 + 'ch' }"
                :placeholder="internalChips.length > 0 ? '' : '...'" v-model="inputValue" @keydown="handleKeyDown">
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue'

const props = defineProps({
    modelValue: {
        type: String,
        default: ""
    },
    chips: {
        type: Array,
        default: () => []
    },
    separator: {
        type: String,
        default: ','
    },
    chipDelete: {
        type: Boolean,
        default: true
    },
    chipColor: {
        type: [String, Array],
        default: "#2563eb" // Default blue color
    },
    chipTextColor: {
        type: [String, Array],
        default: "white" // Default white text color
    },
    showDelete: {
        type: Boolean,
        default: true
    },
    editable: {
        type: Boolean,
        default: true
    },
    size: {
        type: String,
        default: "medium"
    },
    showBorder: {
        type: Boolean,
        default: false
    },
    wrap: {
        type: Boolean,
        default: true
    }

})

const chipInput = ref(null)
const chipsHolder = ref(null)
const visibleChipCount = ref(0)
const hasOverflow = ref(false)


const onClick = (event) => {
    // When clicking on the container (but not on chips), focus the input element
    // Only focus if we're in editable mode and not clicking on a chip
    if (props.editable && chipInput.value && !event.target.closest('.chip')) {
        chipInput.value.focus();
    }
}

const emit = defineEmits(['update:modelValue', 'update:chips'])
const inputValue = ref('')



const internalChips = computed({
    get: () => {
        // If chips prop is provided and has items, use it
        if (props.chips && props.chips.length > 0) {
            return [...props.chips]
        }
        // Otherwise, fall back to parsing modelValue
        return props.modelValue ? props.modelValue.split(props.separator).filter(c => c.trim() !== '') : []
    },
    set: (val) => {
        emit('update:modelValue', val.join(props.separator))
        emit('update:chips', [...val])
    }
})

// Computed property for visible chips when wrap is false
const visibleChips = computed(() => {
    if (props.wrap) {
        return internalChips.value
    }
    return internalChips.value.slice(0, visibleChipCount.value)
})

// Computed property for overflow count
const overflowCount = computed(() => {
    if (props.wrap) {
        return 0
    }
    return Math.max(0, internalChips.value.length - visibleChipCount.value)
})

// Function to calculate visible chips based on available space
const calculateVisibleChips = () => {
    if (props.wrap || !chipsHolder.value) {
        return
    }
    
    // Get container width
    const containerWidth = chipsHolder.value.offsetWidth
    
    // Estimate input width (minimum 50px for input + some padding)
    const inputWidth = props.editable ? Math.max(50, (inputValue.value.length + 3) * 8) : 0
    
    // Estimate overflow indicator width (approximately 40px)
    const overflowIndicatorWidth = 40
    
    // Available width for chips
    const availableWidth = containerWidth - inputWidth - overflowIndicatorWidth - 20 // 20px buffer
    
    // Estimate chip widths (rough calculation)
    let usedWidth = 0
    let count = 0
    
    for (let i = 0; i < internalChips.value.length; i++) {
        // Rough estimation: character count * 8px + padding (40px)
        const chipWidth = internalChips.value[i].length * 8 + 40
        
        if (usedWidth + chipWidth > availableWidth) {
            break
        }
        
        usedWidth += chipWidth + 8 // 8px gap
        count++
    }
    
    visibleChipCount.value = Math.max(0, count)
    hasOverflow.value = count < internalChips.value.length
}

// Watch for changes that require recalculation
watch([internalChips, () => props.wrap, inputValue], () => {
    if (!props.wrap) {
        nextTick(() => {
            calculateVisibleChips()
        })
    }
}, { deep: true })

// Initial calculation on mount
onMounted(() => {
    if (!props.wrap) {
        nextTick(() => {
            calculateVisibleChips()
        })
    }
})

const addChip = () => {
    if (inputValue.value.trim() !== '') {
        const newChips = [...internalChips.value, inputValue.value.trim()]
        internalChips.value = newChips
        inputValue.value = ''
    }
}

const handleKeyDown = (event) => {
    if (event.key === props.separator || event.key === 'Enter') {
        event.preventDefault()
        addChip()
    } else if (event.key === 'Backspace' && inputValue.value === '') {
        event.preventDefault()
        if (internalChips.value.length > 0) {
            const newChips = [...internalChips.value]
            const newVal = newChips.pop()
            internalChips.value = newChips
            if (!props.chipDelete) {
                inputValue.value = newVal.substring(0, newVal.length)
            }

        }
    }
}

const removeChip = (index) => {
    if (!props.chipDelete) return
    const newChips = [...internalChips.value]
    newChips.splice(index, 1)
    internalChips.value = newChips
}

/**
 * Returns the background color for a chip based on its index
 * If chipColor is a string, returns that color
 * If chipColor is an array, cycles through the colors based on the index
 */
const getChipColor = (index) => {
    if (Array.isArray(props.chipColor)) {
        // If it's an array, cycle through the colors
        const colorIndex = index % props.chipColor.length
        return props.chipColor[colorIndex]
    }
    // If it's a string, return the string color
    return props.chipColor
}

/**
 * Returns the text color for a chip based on its index
 * If chipTextColor is a string, returns that color
 * If chipTextColor is an array, cycles through the colors based on the index
 */
const getChipTextColor = (index) => {
    if (Array.isArray(props.chipTextColor)) {
        // If it's an array, cycle through the colors
        const colorIndex = index % props.chipTextColor.length
        return props.chipTextColor[colorIndex]
    }
    // If it's a string, return the string color
    return props.chipTextColor
}

</script>

<style scoped>
.chip-input {
    display: flex;
    flex-wrap: wrap;
    margin: 1em;
    padding: 0.3em 1em;
    align-items: center;
    border-radius: 8px;

    &.show-border {
        border: 1px solid red;
    }

    .chips-holder {
        display: flex;
        /* background-color: lightgreen; */
        flex-wrap: wrap;
        gap: 0.5em;
        font-size: 90%;
        width: 100%;

        &.no-wrap {
            flex-wrap: nowrap;
            overflow: hidden;
        }

        .overflow-indicator {
            border-radius: 5px;
            padding: 0.25em 0.5em;
            background-color: #6b7280;
            color: white;
            font-size: 85%;
            font-weight: 500;
            display: flex;
            align-items: center;
            white-space: nowrap;
            flex-shrink: 0;

            &.size-small {
                font-size: 75%;
                padding: 0.2em 0.4em;
            }
        }

        .chip {
            /* Darker blue on hover */
            border-radius: 5px;
            padding: 0.25em 0.5em 0.25em 0.75em;
            display: flex;
            flex-wrap: nowrap;
            white-space: nowrap;
            align-items: center;
            gap: 0.25em;
            transition: background-color 0.2s ease-in-out;
            flex-shrink: 0;

            /* Color is set dynamically */
            &.no-remove {
                padding: 0.25em 0.75em 0.25em 0.75em;
            }

            &:hover {
                filter: brightness(90%);
            }

            &.size-small {
                font-size: 80%;
                font-weight: 400;
                padding: 0.25em 0.5em 0.25em 0.75em;
            }

            button {
                background: none;
                border: none;

                /* Styling for the SVG icon within the remove button */
                .chip-remove-icon {
                    position: relative;
                    top: 2px;
                    width: 0.875em;
                    /* Relative to font size */
                    height: 0.875em;
                    /* Relative to font size */
                    stroke: currentColor;
                    /* Inherit color from parent button */
                }
            }
        }
    }

    input {
        border: none;
        /* background-color: lightgray; */
        /* align-left: center; */
        width: initial;
        padding: 0;
        margin: 0;
        height: 30px;
        outline: none;
        flex-shrink: 1;
        min-width: 50px;
        /* width: 15ch; */
        /* max-width: 1⁄0ch; */
        /* min-width: 2ch; */
        /* field-sizing: content; */

        /* background-color: green; */
    }


}
</style>